import * as React from "react";

import type { ToastActionElement, ToastProps } from "@/components/ui/toast";
import { textoDoAviso, type TipoAviso } from "@/lib/textoDoAviso";

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000000;

type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  /** Parte D1: miolo próprio (o aviso de notificação); o Toaster o monta no lugar do título/descrição/ação. */
  conteudo?: React.ReactNode;
};

const _actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof _actionTypes;

type Action =
  | {
      type: ActionType["ADD_TOAST"];
      toast: ToasterToast;
    }
  | {
      type: ActionType["UPDATE_TOAST"];
      toast: Partial<ToasterToast>;
    }
  | {
      type: ActionType["DISMISS_TOAST"];
      toastId?: ToasterToast["id"];
    }
  | {
      type: ActionType["REMOVE_TOAST"];
      toastId?: ToasterToast["id"];
    };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
      };

    case "DISMISS_TOAST": {
      const { toastId } = action;

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

type Toast = Omit<ToasterToast, "id">;

/** A variante do aviso diz que tipo de texto padrão vale quando o título não é aproveitável. */
function tipoDaVariante(variant: ToasterToast["variant"]): TipoAviso {
  if (variant === "destructive" || variant === "critico") return "error";
  if (variant === "success") return "success";
  if (variant === "warning") return "warning";
  return "message";
}

/**
 * Título e descrição sempre como texto. O Toaster renderiza os dois como filhos; no 422 do
 * FastAPI o `detail` é uma LISTA de objetos (`[{type, loc, msg, input}]`), e o React lança
 * "Objects are not valid as a React child" e derruba a árvore. Só mexe no que veio:
 * `conteudo` (o miolo próprio do aviso do sino) e `action` passam intactos, e quem não traz
 * título continua sem título.
 */
function protegerTextos<T extends Partial<Pick<ToasterToast, "title" | "description">>>(
  props: T,
  variant: ToasterToast["variant"]
): T {
  const tipo = tipoDaVariante(variant);
  // `title` do ToasterToast é `string & ReactNode` (o `title` HTML do Radix cruza com o nosso):
  // monta os textos à parte, como ReactNode, e só no fim devolve no formato de quem chamou.
  const textos: { title?: React.ReactNode; description?: React.ReactNode } = {};
  if (props.title !== undefined && props.title !== null) {
    textos.title = textoDoAviso(props.title, { tipo, obrigatorio: true }) as React.ReactNode;
  }
  if ("description" in props) {
    textos.description = textoDoAviso(props.description, {
      tipo,
      obrigatorio: false,
    }) as React.ReactNode;
  }
  return { ...props, ...textos };
}

function toast({ ...propsDeEntrada }: Toast) {
  const id = genId();
  const props = protegerTextos(propsDeEntrada, propsDeEntrada.variant);

  const update = (novas: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      // Sem variante no update, vale a do aviso original (decide o texto padrão do título).
      toast: { ...protegerTextos(novas, novas.variant ?? props.variant), id },
    });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });

  return {
    id: id,
    dismiss,
    update,
  };
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  };
}

/**
 * Esvazia todos os avisos do estado (usado pelo limite de erro do Toaster). Despacha
 * `REMOVE_TOAST` sem id: o `DISMISS` só marca `open: false` e deixa o toast no estado por
 * `TOAST_REMOVE_DELAY` (1.000.000 ms), e um aviso que quebrou o render continuaria sendo
 * renderizado de novo a cada remontagem.
 */
function limparAvisos() {
  dispatch({ type: "REMOVE_TOAST" });
}

/**
 * O toast `id` ainda está na tela? (parte D1) Com `TOAST_LIMIT = 1`, um `toast()` de outra
 * tela expulsa o anterior do estado na hora, e `update()` num id expulso não faz nada nem
 * avisa — o aviso agrupado sumiria em silêncio.
 */
function toastEstaAberto(id: string): boolean {
  return memoryState.toasts.some((t) => t.id === id && t.open !== false);
}

export { useToast, toast, toastEstaAberto, limparAvisos };
